import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
  alpha,
  Box,
  IconButton,
  Link,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useAuth } from '@verantos-dev/core-auth';
import { DatasetDetailsModal } from '@verantos-dev/ui-cohort-builder-components';

import { PanelLayoutToggle } from './PanelLayoutToggle';
import type { ProjectPanelLayout } from './projectPanelLayoutModel';
import { SwitchDatasetModal } from './SwitchDatasetModal';

export interface ProjectPageHeaderProps {
  /** Project (study) name — rendered as the header title. */
  projectName?: string;
  /** Whether the project name is still loading (drives the title skeleton). */
  isProjectNameLoading?: boolean;
  /** Release id of the project's dataset — gates the dataset-details link/modal. */
  datasetReleaseId?: string | null;
  /** Display name of the project's dataset. */
  datasetReleaseName?: string | null;
  /** Project being viewed — required to switch its active dataset. */
  projectId?: string;
  /** The project's owning organization. The switch control is hidden unless it
   *  matches the viewer's own org — repointing a project changes the dataset
   *  every cohort and analytical dataset created there afterwards is built on,
   *  which is the owning org's call to make. */
  projectOrganizationId?: string | null;
  /** True while the Research Assistant is mid-turn in this project. Switching
   *  the dataset then would leave the run it is completing on the previous one,
   *  so the control is disabled until it finishes. */
  isResearchAssistantWorking?: boolean;
  /** Current 3-way panel layout (split / RA-only / artifacts-only). */
  panelLayout: ProjectPanelLayout;
  /** Apply a new panel layout. */
  onChangePanelLayout: (layout: ProjectPanelLayout) => void;
}

/**
 * Header strip above the split layout (OR-6123 F3).
 *
 * Left: the project name as the title + an `Active dataset: <name>` link that
 * opens the dataset-details modal, and a Switch dataset action beside it (the
 * breadcrumb moved into each detail page — see F8 `SubPageBreadcrumbHeader`).
 * Right: the 3-way panel-layout toggle (split / RA-only / artifacts-only).
 *
 * TODO: cleanup with research_assistant_gen_3 ff
 */
export const ProjectPageHeader = ({
  projectName,
  isProjectNameLoading,
  datasetReleaseId,
  datasetReleaseName,
  projectId,
  projectOrganizationId,
  isResearchAssistantWorking = false,
  panelLayout,
  onChangePanelLayout,
}: ProjectPageHeaderProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const [isDatasetModalOpen, setIsDatasetModalOpen] = useState(false);
  const [isSwitchDatasetModalOpen, setIsSwitchDatasetModalOpen] = useState(false);

  const { user } = useAuth();

  const hasDataset = Boolean(datasetReleaseId);
  const datasetLabel = datasetReleaseName ?? '—';
  // Deliberately a plain org match, with no internal-user exemption: a Verantos
  // user browsing a customer's project should not be able to repoint it.
  // ``user`` is typed non-optional but the auth context's default value is an
  // empty object, so the org id really can be missing before auth resolves —
  // and a missing id on either side reads as "not ours", so the control never
  // flashes in before ownership is known.
  const isOwnOrganization = Boolean(
    projectOrganizationId && user?.organizationId && projectOrganizationId === user.organizationId
  );
  const showSwitchDataset = Boolean(projectId) && isOwnOrganization;

  return (
    <Box
      data-testid="project-page-header"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        paddingX: 2,
        paddingY: 1.5,
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        flexShrink: 0,
        minHeight: 64,
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {isProjectNameLoading && !projectName ? (
          <Skeleton
            variant="text"
            width={240}
            height={32}
            data-testid="project-page-title-loading"
          />
        ) : (
          <Typography
            variant="h5"
            component="h1"
            noWrap
            data-testid="project-page-title"
            sx={{ flexShrink: 0, maxWidth: '50%' }}
          >
            {projectName ?? '—'}
          </Typography>
        )}
        {/* The dataset affordance resolves together with the project name, so
            hide it entirely while the title skeleton is showing — otherwise the
            header reads as a half-loaded `Dataset: —` beside the skeleton. */}
        {isProjectNameLoading && !projectName ? null : (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle1" color="text.secondary" sx={{ flexShrink: 0 }}>
              {formatMessage({ id: 'projectPage.header.activeDatasetLabel' })}
            </Typography>
            {hasDataset ? (
              <Link
                component="button"
                type="button"
                variant="subtitle1"
                underline="hover"
                onClick={() => setIsDatasetModalOpen(true)}
                data-testid="project-page-dataset-link"
                sx={{
                  color: theme.palette.primary.main,
                  cursor: 'pointer',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {datasetLabel}
              </Link>
            ) : (
              <Typography
                variant="subtitle1"
                color="text.primary"
                noWrap
                data-testid="project-page-dataset-name"
              >
                {datasetLabel}
              </Typography>
            )}
            {showSwitchDataset ? (
              <Tooltip
                title={formatMessage({
                  id: isResearchAssistantWorking
                    ? 'projectPage.header.switchDatasetDisabledTooltip'
                    : 'projectPage.header.switchDatasetButton',
                })}
              >
                {/* A disabled MUI button emits no pointer events, so the tooltip
                    needs a wrapper to hang the listener on — which is the case
                    the tooltip exists for. */}
                {/* Padding, not margin: the parent Stack's `spacing` rule
                    (`.MuiStack-root > :not(style) ~ :not(style)`) outranks a
                    child's own `sx` margin, so `ml` here is silently dropped. */}
                <Box component="span" sx={{ flexShrink: 0, display: 'inline-flex', pl: 1 }}>
                  <IconButton
                    size="small"
                    color="primary"
                    disabled={isResearchAssistantWorking}
                    onClick={() => setIsSwitchDatasetModalOpen(true)}
                    data-testid="project-page-switch-dataset-button"
                    // The icon carries no text, so the tooltip copy is also the
                    // accessible name rather than only a hover affordance.
                    aria-label={formatMessage({ id: 'projectPage.header.switchDatasetButton' })}
                    sx={{
                      // MUI's default 4% hover on a primary IconButton is
                      // near-invisible on this header's paper background.
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.12),
                      },
                    }}
                  >
                    <SwapHorizIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Tooltip>
            ) : null}
          </Stack>
        )}
      </Box>
      <PanelLayoutToggle layout={panelLayout} onChange={onChangePanelLayout} />
      {isDatasetModalOpen && datasetReleaseId ? (
        <DatasetDetailsModal
          releaseId={datasetReleaseId}
          releaseName={datasetReleaseName ?? ''}
          onClose={() => setIsDatasetModalOpen(false)}
        />
      ) : null}
      {isSwitchDatasetModalOpen && projectId ? (
        <SwitchDatasetModal
          projectId={projectId}
          currentDatasetReleaseId={datasetReleaseId}
          currentDatasetReleaseName={datasetReleaseName}
          onClose={() => setIsSwitchDatasetModalOpen(false)}
        />
      ) : null}
    </Box>
  );
};
